import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  fetchProfile,
  PROFILE_QUERY_KEY,
  PROFILE_STALE_TIME,
  type MyProfile,
} from '../lib/profile';

type RuntimeConfig = {
  FULLSTORY_ENABLED?: string;
  FULLSTORY_ORG_ID?: string;
};

export function configuredFullStoryOrgId(config: RuntimeConfig): string | null {
  const orgId = config.FULLSTORY_ORG_ID?.trim();
  return config.FULLSTORY_ENABLED === 'true' && orgId ? orgId : null;
}

const pendingSessions = new Set<string>();

export function FullStorySession() {
  const config = (window as Window & { __CONFIG__?: RuntimeConfig }).__CONFIG__ ?? {};
  const orgId = configuredFullStoryOrgId(config);
  const { data: profile } = useQuery<MyProfile>({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    staleTime: PROFILE_STALE_TIME,
    enabled: Boolean(orgId),
  });

  useEffect(() => {
    if (!orgId || !profile?.userId) return;

    let stopped = false;
    let sdk: typeof import('@fullstory/browser') | undefined;
    let disconnectObserver: (() => void) | undefined;

    void import('@fullstory/browser')
      .then(async (loaded) => {
        if (stopped) return;
        sdk = loaded;
        const restarting = loaded.isInitialized();
        if (!restarting) loaded.init({ orgId, startCaptureManually: true });
        loaded.FullStory('setIdentity', {
          uid: `argus:${profile.userId}`,
          properties: {
            displayName: profile.displayName?.trim() || profile.email,
            email: profile.email,
          },
        });
        loaded.FullStory(restarting ? 'restart' : 'start');
        const observer = await loaded.FullStory('observeAsync', {
          type: 'start',
          callback: () => {
            if (stopped) return;
            const sessionUrl = loaded.FullStory('getSession', { format: 'url' });
            if (!sessionUrl || pendingSessions.has(sessionUrl)
              || sessionStorage.getItem('fullstory-notified-session') === sessionUrl) return;
            pendingSessions.add(sessionUrl);
            void api.post('/notifications/fullstory', {
              sessionUrl,
              displayName: profile.displayName?.trim() || profile.email,
            })
              .then(() => sessionStorage.setItem('fullstory-notified-session', sessionUrl))
              .catch(() => {})
              .finally(() => pendingSessions.delete(sessionUrl));
          },
        });
        if (stopped) observer.disconnect();
        else disconnectObserver = () => observer.disconnect();
      })
      .catch(() => {});

    return () => {
      stopped = true;
      disconnectObserver?.();
      sdk?.FullStory('shutdown');
    };
  }, [orgId, profile?.userId, profile?.displayName, profile?.email]);

  return null;
}
